<template>
  <SidebarComponent />
  <div class="dashboard-layout">
    <div class="content-container">
      <div class="card-container analytics-page">
        <div class="titolo-card analytics-hero">
          <div>
            <h1>Analytics ordini</h1>
            <p class="subtitle">
              Dashboard read-only per analizzare ordini, prodotti, quantita e incasso per intervallo data.
            </p>
          </div>
          <div class="hero-badge">
            <span class="hero-badge__value">{{ formatCurrency(summary.revenue) }}</span>
            <span class="hero-badge__label">Incasso nel range</span>
          </div>
        </div>

        <section class="card-surface filters-card">
          <div class="filters-grid">
            <div class="field-group">
              <label for="analytics-from">Dal</label>
              <input id="analytics-from" v-model="filters.fromDate" type="date" />
            </div>
            <div class="field-group">
              <label for="analytics-to">Al</label>
              <input id="analytics-to" v-model="filters.toDate" type="date" />
            </div>
            <div class="field-group">
              <label for="analytics-source">Sorgente</label>
              <select id="analytics-source" v-model="filters.source">
                <option value="">Tutte</option>
                <option v-for="option in sourceOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
            <div class="field-group">
              <label for="analytics-status">Stato</label>
              <select id="analytics-status" v-model="filters.statusMode">
                <option v-for="option in statusModeOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
            <div class="field-group">
              <label for="analytics-category">Categoria</label>
              <select id="analytics-category" v-model="filters.categoryMode">
                <option v-for="option in categoryOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
            <div class="field-group field-group--wide">
              <label for="analytics-product">Cerca prodotto</label>
              <input
                id="analytics-product"
                v-model.trim="filters.product"
                type="search"
                placeholder="es. FC4 Plus"
              />
            </div>
            <div class="field-group field-group--actions">
              <label>Azioni</label>
              <div class="filters-actions">
                <button
                  type="button"
                  class="btn ghost btn--icon"
                  :disabled="loading"
                  @click="resetFilters"
                  title="Reset filtri"
                  aria-label="Reset filtri"
                >
                  <span class="material-icons" aria-hidden="true">restart_alt</span>
                </button>
                <button
                  type="button"
                  class="btn primary btn--icon staff-button-neutral"
                  :disabled="!canLoad"
                  @click="loadOrders"
                  :title="loading ? 'Aggiorna ordini' : 'Ricarica ordini'"
                  :aria-label="loading ? 'Aggiorna ordini' : 'Ricarica ordini'"
                >
                  <span
                    class="material-icons"
                    :class="{ 'icon-spin': loading }"
                    aria-hidden="true"
                  >
                    {{ loading ? 'autorenew' : 'refresh' }}
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div class="filters-note">
            <span v-if="hasLoaded && lastUpdatedAt">Ultimo aggiornamento: {{ formatDateTime(lastUpdatedAt) }}</span>
            <span v-if="progressText">{{ progressText }}</span>
            <span v-if="hasLoaded && !loading && rawLines.length">Linee prodotto elaborate: {{ rawLines.length }}</span>
          </div>
        </section>

        <section class="card-surface" v-if="error">
          <p class="error-state">{{ error }}</p>
        </section>

        <section class="card-surface" v-else-if="!hasLoaded">
          <p class="muted">Imposta un intervallo data `dal / al` e premi `Ricarica ordini`.</p>
        </section>

        <section class="card-surface" v-else-if="loading && !rawLines.length">
          <p class="muted">Caricamento ordini in corso...</p>
        </section>

        <template v-else>
          <section class="card-surface accordion">
            <button
              type="button"
              class="accordion__summary"
              :aria-expanded="accordions.overview ? 'true' : 'false'"
              @click="accordions.overview = !accordions.overview"
            >
              <div class="accordion__heading">
                <h2>Panoramica KPI</h2>
                <p>Metriche sintetiche del range selezionato. La sezione parte chiusa per lasciare piu spazio ai blocchi principali.</p>
              </div>
              <span class="material-icons accordion__icon" :class="{ 'accordion__icon--open': accordions.overview }">expand_more</span>
            </button>
            <div v-show="accordions.overview" class="accordion__content">
              <div class="kpi-grid">
                <article class="kpi-card">
                  <span class="kpi-card__label">Incasso</span>
                  <strong class="kpi-card__value">{{ formatCurrency(summary.revenue) }}</strong>
                  <small class="kpi-card__meta">Valore netto allocato sulle righe prodotto.</small>
                </article>
                <article class="kpi-card">
                  <span class="kpi-card__label">Ordini unici</span>
                  <strong class="kpi-card__value">{{ formatNumber(summary.orders) }}</strong>
                  <small class="kpi-card__meta">Ordini inclusi nei filtri correnti.</small>
                </article>
                <article class="kpi-card">
                  <span class="kpi-card__label">Prodotti venduti</span>
                  <strong class="kpi-card__value">{{ formatNumber(summary.quantity) }}</strong>
                  <small class="kpi-card__meta">Somma quantita delle righe prodotto.</small>
                </article>
                <article class="kpi-card">
                  <span class="kpi-card__label">Prodotti unici</span>
                  <strong class="kpi-card__value">{{ formatNumber(summary.uniqueProducts) }}</strong>
                  <small class="kpi-card__meta">Aggregati per nome prodotto.</small>
                </article>
                <article class="kpi-card">
                  <span class="kpi-card__label">Ticket medio</span>
                  <strong class="kpi-card__value">{{ formatCurrency(summary.avgOrderValue) }}</strong>
                  <small class="kpi-card__meta">Incasso medio per ordine nel range.</small>
                </article>
              </div>
            </div>
          </section>

          <section class="card-surface" v-if="sourceSummary.length">
            <div class="section-heading">
              <div>
                <h2>Sorgenti</h2>
                <p>Incasso e quantita divisi per origine ordine.</p>
              </div>
            </div>
            <div class="source-grid">
              <article v-for="source in sourceSummary" :key="source.source" class="source-card">
                <div class="source-card__head">
                  <span class="badge-src" :data-src="source.source">{{ source.label }}</span>
                  <strong>{{ formatCurrency(source.revenue) }}</strong>
                </div>
                <div class="source-card__meta">
                  <span>{{ formatNumber(source.orders) }} ordini</span>
                  <span>{{ formatNumber(source.quantity) }} pz</span>
                  <span>{{ formatPercent(source.revenueShare) }} del totale</span>
                </div>
              </article>
            </div>
          </section>

          <section class="chart-grid">
            <article class="card-surface chart-card">
              <div class="section-heading">
                <div>
                  <h2>Top prodotti per incasso</h2>
                  <p>I primi {{ topRevenueProducts.length }} prodotti ordinati per ricavo netto.</p>
                </div>
              </div>
              <div v-if="topRevenueProducts.length" class="bar-chart-list">
                <div v-for="item in topRevenueProducts" :key="item.key" class="bar-chart-item">
                  <div class="bar-chart-item__head">
                    <div>
                      <strong>{{ item.productName }}</strong>
                      <small>{{ formatNumber(item.quantity) }} pz · {{ formatNumber(item.orderCount) }} ordini</small>
                    </div>
                    <span>{{ formatCurrency(item.revenue) }}</span>
                  </div>
                  <div class="bar-track">
                    <span
                      class="bar-fill bar-fill--revenue"
                      :style="{ width: `${ratioPercent(item.revenue, maxRevenueValue)}%` }"
                    />
                  </div>
                </div>
              </div>
              <p v-else class="muted">Nessun dato disponibile con i filtri correnti.</p>
            </article>

            <article class="card-surface chart-card">
              <div class="section-heading">
                <div>
                  <h2>Top prodotti per quantita</h2>
                  <p>I primi {{ topQuantityProducts.length }} prodotti per pezzi venduti.</p>
                </div>
              </div>
              <div v-if="topQuantityProducts.length" class="bar-chart-list">
                <div v-for="item in topQuantityProducts" :key="item.key" class="bar-chart-item">
                  <div class="bar-chart-item__head">
                    <div>
                      <strong>{{ item.productName }}</strong>
                      <small>{{ formatCurrency(item.revenue) }} · {{ formatNumber(item.orderCount) }} ordini</small>
                    </div>
                    <span>{{ formatNumber(item.quantity) }} pz</span>
                  </div>
                  <div class="bar-track">
                    <span
                      class="bar-fill bar-fill--quantity"
                      :style="{ width: `${ratioPercent(item.quantity, maxQuantityValue)}%` }"
                    />
                  </div>
                </div>
              </div>
              <p v-else class="muted">Nessun dato disponibile con i filtri correnti.</p>
            </article>
          </section>

          <section class="card-surface accordion trend-section">
            <button
              type="button"
              class="accordion__summary"
              :aria-expanded="accordions.trend ? 'true' : 'false'"
              @click="accordions.trend = !accordions.trend"
            >
              <div class="accordion__heading">
                <h2>Trend mensile</h2>
                <p>Vista mensile del range selezionato, con confronto tra incasso e quantita.</p>
              </div>
              <span class="material-icons accordion__icon" :class="{ 'accordion__icon--open': accordions.trend }">expand_more</span>
            </button>
            <div v-show="accordions.trend" class="accordion__content">
              <div v-if="monthlyTrend.length" class="month-chart-list">
                <div v-for="month in monthlyTrend" :key="month.key" class="month-chart-row">
                  <div class="month-chart-row__label">
                    <strong>{{ month.label }}</strong>
                    <small>{{ formatNumber(month.orders) }} ordini</small>
                  </div>
                  <div class="month-chart-row__bars">
                    <div class="month-bar-block">
                      <div class="month-bar-block__meta">
                        <span>Incasso</span>
                        <strong>{{ formatCurrency(month.revenue) }}</strong>
                      </div>
                      <div class="bar-track">
                        <span
                          class="bar-fill bar-fill--revenue"
                          :style="{ width: `${ratioPercent(month.revenue, maxMonthRevenue)}%` }"
                        />
                      </div>
                    </div>
                    <div class="month-bar-block">
                      <div class="month-bar-block__meta">
                        <span>Quantita</span>
                        <strong>{{ formatNumber(month.quantity) }} pz</strong>
                      </div>
                      <div class="bar-track bar-track--soft">
                        <span
                          class="bar-fill bar-fill--quantity"
                          :style="{ width: `${ratioPercent(month.quantity, maxMonthQuantity)}%` }"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p v-else class="muted">Nessun mese disponibile con i filtri correnti.</p>
            </div>
          </section>

          <section class="card-surface accordion focus-card">
            <button
              type="button"
              class="accordion__summary"
              :aria-expanded="accordions.focus ? 'true' : 'false'"
              @click="accordions.focus = !accordions.focus"
            >
              <div class="accordion__heading">
                <h2>Focus prodotto</h2>
                <p>Seleziona un prodotto per vedere quanti pezzi e quanto incasso ha generato, anche per mese.</p>
              </div>
              <span class="material-icons accordion__icon" :class="{ 'accordion__icon--open': accordions.focus }">expand_more</span>
            </button>
            <div v-show="accordions.focus" class="accordion__content">
              <div class="focus-filters">
                <div class="field-group field-group--wide">
                  <label for="focus-product">Prodotto</label>
                  <select id="focus-product" v-model="selectedProductKey">
                    <option v-for="product in productRows" :key="product.key" :value="product.key">
                      {{ product.productName }} · {{ formatCurrency(product.revenue) }} · {{ formatNumber(product.quantity) }} pz
                    </option>
                  </select>
                </div>
                <div class="field-group">
                  <label for="focus-month">Mese</label>
                  <select id="focus-month" v-model="selectedMonthKey" :disabled="!selectedProduct">
                    <option value="all">Tutto il range</option>
                    <option
                      v-for="month in selectedProductMonths"
                      :key="month.key"
                      :value="month.key"
                    >
                      {{ month.label }}
                    </option>
                  </select>
                </div>
              </div>

              <template v-if="selectedProduct">
                <div class="focus-summary">
                  <article class="focus-stat">
                    <span>Prodotto</span>
                    <strong>{{ selectedProduct.productName }}</strong>
                    <small>{{ selectedProduct.skuLabel }}</small>
                  </article>
                  <article class="focus-stat">
                    <span>Quantita</span>
                    <strong>{{ formatNumber(focusStats.quantity) }} pz</strong>
                    <small>{{ formatNumber(focusStats.orders) }} ordini</small>
                  </article>
                  <article class="focus-stat">
                    <span>Incasso</span>
                    <strong>{{ formatCurrency(focusStats.revenue) }}</strong>
                    <small>{{ focusStats.scopeLabel }}</small>
                  </article>
                </div>

                <div class="focus-month-table-wrap">
                  <table class="products-table products-table--months">
                    <thead>
                      <tr>
                        <th>Mese</th>
                        <th>Ordini</th>
                        <th>Quantita</th>
                        <th>Incasso</th>
                        <th>Quota sul range</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="month in selectedProductMonths" :key="month.key">
                        <td>{{ month.label }}</td>
                        <td>{{ formatNumber(month.orders) }}</td>
                        <td>{{ formatNumber(month.quantity) }}</td>
                        <td>{{ formatCurrency(month.revenue) }}</td>
                        <td>{{ formatPercent(month.revenueShare) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
              <p v-else class="muted">Nessun prodotto disponibile nel range selezionato.</p>
            </div>
          </section>

          <section class="card-surface accordion comparison-card" v-if="comparisonMonths.length && productComparisonRows.length">
            <button
              type="button"
              class="accordion__summary"
              :aria-expanded="accordions.comparison ? 'true' : 'false'"
              @click="accordions.comparison = !accordions.comparison"
            >
              <div class="accordion__heading">
                <h2>Confronto prodotti per mese</h2>
                <p>Grafico a linee con punti sulla quantita venduta per mese. Mostra il prodotto selezionato e i principali prodotti del range.</p>
              </div>
              <span class="material-icons accordion__icon" :class="{ 'accordion__icon--open': accordions.comparison }">expand_more</span>
            </button>
            <div v-show="accordions.comparison" class="accordion__content">
              <div v-if="comparisonLineSeries.length && comparisonChartMonths.length" class="comparison-chart-shell">
                <svg class="comparison-chart" :viewBox="comparisonChartViewBox" preserveAspectRatio="none" role="img" aria-label="Andamento prodotti per mese">
                  <g v-for="tick in comparisonChartTicks" :key="`tick-${tick.y}`">
                    <line
                      x1="48"
                      :x2="COMPARISON_CHART_WIDTH - 22"
                      :y1="tick.y"
                      :y2="tick.y"
                      class="comparison-chart__grid"
                    />
                    <text x="40" :y="tick.y + 4" class="comparison-chart__tick">{{ formatNumber(tick.value) }}</text>
                  </g>

                  <line
                    x1="48"
                    :x2="COMPARISON_CHART_WIDTH - 22"
                    :y1="comparisonChartBottomY"
                    :y2="comparisonChartBottomY"
                    class="comparison-chart__axis"
                  />

                  <g v-for="month in comparisonChartMonths" :key="`axis-${month.key}`">
                    <line
                      :x1="month.x"
                      :x2="month.x"
                      y1="18"
                      :y2="comparisonChartBottomY"
                      class="comparison-chart__guide"
                    />
                    <text :x="month.x" :y="COMPARISON_CHART_HEIGHT - 14" class="comparison-chart__label">{{ month.shortLabel }}</text>
                  </g>

                  <g v-for="series in comparisonLineSeries" :key="`series-${series.key}`">
                    <path :d="series.path" :stroke="series.color" class="comparison-chart__line" />
                    <circle
                      v-for="point in series.points"
                      :key="`${series.key}-${point.key}`"
                      :cx="point.x"
                      :cy="point.y"
                      r="5"
                      :fill="series.color"
                      class="comparison-chart__point"
                    />
                  </g>
                </svg>
              </div>

              <div class="comparison-legend">
                <button
                  v-for="series in comparisonLineSeries"
                  :key="`legend-${series.key}`"
                  type="button"
                  class="comparison-legend__item"
                  :class="{ 'comparison-legend__item--active': series.key === selectedProductKey }"
                  @click="selectedProductKey = series.key"
                >
                  <span class="comparison-legend__swatch" :style="{ backgroundColor: series.color }" />
                  <span class="comparison-legend__copy">
                    <strong>{{ series.productName }}</strong>
                    <small>{{ formatNumber(series.quantity) }} pz · {{ formatCurrency(series.revenue) }}</small>
                  </span>
                </button>
              </div>
            </div>
          </section>

          <section class="card-surface accordion">
            <button
              type="button"
              class="accordion__summary"
              :aria-expanded="accordions.detail ? 'true' : 'false'"
              @click="accordions.detail = !accordions.detail"
            >
              <div class="accordion__heading">
                <h2>Dettaglio prodotti</h2>
                <p>Tabella completa ordinata per incasso, utile per leggere il dato preciso prodotto per prodotto.</p>
              </div>
              <span class="material-icons accordion__icon" :class="{ 'accordion__icon--open': accordions.detail }">expand_more</span>
            </button>
            <div v-show="accordions.detail" class="accordion__content">
              <div v-if="productRows.length" class="table-wrap">
                <table class="products-table">
                  <thead>
                    <tr>
                      <th>Prodotto</th>
                      <th>SKU / ID</th>
                      <th>Ordini</th>
                      <th>Quantita</th>
                      <th>Incasso</th>
                      <th>Primo acquisto</th>
                      <th>Ultimo acquisto</th>
                      <th>Sorgenti</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="product in productRows" :key="product.key">
                      <td class="product-name-cell">
                        <strong>{{ product.productName }}</strong>
                      </td>
                      <td class="mono">{{ product.skuLabel }}</td>
                      <td>{{ formatNumber(product.orderCount) }}</td>
                      <td>{{ formatNumber(product.quantity) }}</td>
                      <td>{{ formatCurrency(product.revenue) }}</td>
                      <td>{{ product.firstOrderLabel }}</td>
                      <td>{{ product.lastOrderLabel }}</td>
                      <td>
                        <div class="source-badges">
                          <span
                            v-for="source in product.sources"
                            :key="`${product.key}-${source}`"
                            class="badge-src"
                            :data-src="source"
                          >
                            {{ sourceLabel(source) }}
                          </span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="muted">Nessun prodotto trovato con i filtri correnti.</p>
            </div>
          </section>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { collection, getDocs, getFirestore, limit, orderBy, query, startAfter, where } from 'firebase/firestore';
import SidebarComponent from '../SidebarComponent.vue';

const ORDER_PAGE_SIZE = 400;
const TOP_PRODUCTS_LIMIT = 10;
const COMPARISON_VISUAL_LIMIT = 8;
const COMPARISON_CHART_WIDTH = 920;
const COMPARISON_CHART_HEIGHT = 320;
const COMPARISON_CHART_PADDING = Object.freeze({
  top: 18,
  right: 22,
  bottom: 42,
  left: 48,
});
const COMPARISON_LINE_COLORS = Object.freeze([
  '#2563eb',
  '#16a34a',
  '#f97316',
  '#8b5cf6',
  '#e11d48',
  '#0f766e',
  '#d97706',
  '#0891b2',
]);
const SOURCE_LABELS = {
  woocommerce: 'WooCommerce',
  legacy: 'Legacy',
  manuale: 'Manuale',
  importato: 'Importato',
};

const pad2 = (value) => String(value).padStart(2, '0');
const roundCurrency = (value) => Math.round((Number(value) || 0) * 100) / 100;

const defaultDateRange = () => ({
  from: '',
  to: '',
});

const monthKeyFromMs = (ms) => {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;
};

const monthLabelFromKey = (key) => {
  const match = String(key || '').match(/^(\d{4})-(\d{2})$/);
  if (!match) return key || '—';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
  if (Number.isNaN(date.getTime())) return key || '—';
  return date.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
};

const monthShortLabelFromKey = (key) => {
  const match = String(key || '').match(/^(\d{4})-(\d{2})$/);
  if (!match) return key || '—';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
  if (Number.isNaN(date.getTime())) return key || '—';
  const monthLabel = date.toLocaleDateString('it-IT', { month: 'short' }).replace('.', '');
  return `${monthLabel} ${String(match[1]).slice(-2)}`;
};

const normalizeText = (value) => (value == null ? '' : String(value).trim().toLowerCase());
const normalizeKey = (value) => (value == null ? '' : String(value).trim().toLowerCase());

const firstNonEmpty = (values = []) => {
  for (const value of values) {
    if (value == null) continue;
    const trimmed = String(value).trim();
    if (trimmed) return trimmed;
  }
  return '';
};

const splitFullName = (fullName = '') => {
  const trimmed = String(fullName || '').trim();
  if (!trimmed) return { name: '', surname: '' };
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (!parts.length) return { name: '', surname: '' };
  if (parts.length === 1) return { name: parts[0], surname: '' };
  const name = parts.shift();
  return { name: name || '', surname: parts.join(' ') };
};

const formatLabel = (value) => {
  if (!value) return '—';
  return value
    .toString()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const createEmptyProductIndex = () => ({
  byId: Object.create(null),
  bySku: Object.create(null),
  byName: Object.create(null),
});

const buildProductIndex = (products = []) => {
  const index = createEmptyProductIndex();
  products.forEach((product) => {
    if (!product) return;
    const idKey = normalizeKey(product.id);
    if (idKey) index.byId[idKey] = product;
    const skuKey = normalizeKey(product.sku);
    if (skuKey) index.bySku[skuKey] = product;
    const nameKey = normalizeKey(product.name);
    if (nameKey) index.byName[nameKey] = product;
  });
  return index;
};

const toDateSafe = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') {
    try {
      const date = value.toDate();
      return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
    } catch {
      return null;
    }
  }
  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseOrderDate = (order = {}) => {
  const candidates = [
    order.orderDate,
    order.order_date,
    order.createdAt,
    order.creationDate,
    order.importedAt,
    order.updatedAt,
    order.updated_at,
  ];
  for (const value of candidates) {
    const date = toDateSafe(value);
    if (date) return date;
  }
  return null;
};

const isWithinDate = (order = {}, fromDate = '', toDate = '') => {
  const orderDate = parseOrderDate(order);
  if (!orderDate) return false;
  const start = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
  const end = toDate ? new Date(`${toDate}T23:59:59`) : null;
  if (start && orderDate < start) return false;
  if (end && orderDate > end) return false;
  return true;
};

const composeOrderDateBoundary = (date, boundary) => {
  if (!date) return '';
  return boundary === 'start' ? `${date} 00:00:00` : `${date} 23:59:59`;
};

const composeImportedBoundary = (date, boundary) => {
  if (!date) return '';
  return boundary === 'start'
    ? `${date}T00:00:00.000Z`
    : `${date}T23:59:59.999Z`;
};

const sourceLabel = (value) => SOURCE_LABELS[value] || (value || 'Altro');

const normalizeSource = (order = {}, docId = '') => {
  const explicit = normalizeText(order.source || order._source);
  const origin = normalizeText(order.origin || order.importOrigin || order.importedFrom);
  const id = normalizeText(docId || order.orderId || '');

  if (['manuale', 'manual'].includes(explicit)) return 'manuale';
  if (explicit === 'legacy') return 'legacy';
  if (['woocommerce', 'woo', 'wc'].includes(explicit)) return 'woocommerce';
  if (['importato', 'mysql'].includes(explicit)) return 'importato';

  if (id.startsWith('manual_')) return 'manuale';
  if (id.startsWith('legacy_')) return 'legacy';
  if (id.startsWith('export_')) return 'woocommerce';
  if (id.startsWith('mysql_')) return 'importato';
  if (origin === 'mysql' || origin.includes('import')) return 'importato';
  if (order.createdBy) return 'manuale';
  return 'woocommerce';
};

const safeProducts = (order = {}) => {
  const candidates = [order.products, order.line_items, order.lineItems, order.items];
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length) return candidate;
  }
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }
  return [];
};

const resolveLineNameCandidate = (line = {}) => firstNonEmpty([
  line.name,
  line.title,
  line.productName,
  line.product_name,
  line.product_title,
  line.item_name,
  line.itemName,
]);

const resolveLineDisplayName = (line = {}, catalogProduct = null) => {
  const fromLine = resolveLineNameCandidate(line);
  if (fromLine) return fromLine;
  if (catalogProduct?.name) return catalogProduct.name;
  return 'Senza nome';
};

const resolveLineQuantity = (line = {}) => {
  const candidates = [line.quantity, line.qty, line.count, line.amount_qty, line.line_qty];
  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed !== 0) return Math.abs(parsed);
  }
  return 1;
};

const resolveLineUnitPrice = (line = {}) => {
  const candidates = [line.price, line.unitPrice, line.unit_price, line.amount];
  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

const resolveLineTotal = (line = {}, quantity = 1, unitPrice = 0) => {
  const totalCandidates = [line.total, line.line_total, line.total_price, line.line_subtotal, line.subtotal];
  for (const candidate of totalCandidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed)) return parsed;
  }
  return Number((Number(unitPrice) * Number(quantity || 0)).toFixed(2));
};

const resolveLineAmount = (line = {}) => {
  const amountFields = ['total', 'line_total', 'subtotal', 'amount', 'line_subtotal', 'row_total'];
  for (const field of amountFields) {
    const raw = Number(line[field]);
    if (Number.isFinite(raw) && raw !== 0) return raw;
  }
  const priceFields = ['price', 'unit_price', 'line_price'];
  for (const field of priceFields) {
    const raw = Number(line[field]);
    if (Number.isFinite(raw) && raw !== 0) {
      return raw * resolveLineQuantity(line);
    }
  }
  const fallbackPrice = Number(line.price);
  if (Number.isFinite(fallbackPrice)) {
    return fallbackPrice * resolveLineQuantity(line);
  }
  return 0;
};

const resolveLineSku = (line = {}) => firstNonEmpty([line.sku, line.SKU, line.productSku]);

const resolveLineProductId = (line = {}) => firstNonEmpty([
  line.productId,
  line.product_id,
  line.id,
  line.itemId,
  line.catalogId,
]);

const matchCatalogProduct = (line = {}, productIndex = createEmptyProductIndex()) => {
  const { byId, bySku, byName } = productIndex;
  const idCandidates = [
    line.productId,
    line.product_id,
    line.id,
    line.itemId,
    line.catalogId,
  ];
  for (const candidate of idCandidates) {
    const key = normalizeKey(candidate);
    if (key && byId[key]) return byId[key];
  }
  const skuCandidates = [line.sku, line.SKU, line.productSku];
  for (const candidate of skuCandidates) {
    const key = normalizeKey(candidate);
    if (key && bySku[key]) return bySku[key];
  }
  const nameKey = normalizeKey(resolveLineNameCandidate(line));
  if (nameKey && byName[nameKey]) return byName[nameKey];
  return null;
};

const hasPtQuantity = (entity = {}) => {
  if (!entity || typeof entity !== 'object') return false;
  const fields = [
    'pt_quantity',
    'ptQty',
    'ptqty',
    'ptSessions',
    'pt_sessions',
    'bonus_pt',
  ];
  return fields.some((field) => {
    const value = Number(entity[field]);
    return Number.isFinite(value) && value > 0;
  });
};

const isPtCategoryKey = (raw) => {
  const direct = normalizeKey(raw);
  if (!direct) return false;
  if (direct === 'pt') return true;
  const presets = new Set([
    'servizi_pt',
    'servizi-pt',
    'pacchetti_pt',
    'pt_pack',
    'sedute_pt',
    'sedute-pt',
    'pt_studio',
    'studio_pt',
  ]);
  if (presets.has(direct)) return true;
  if (direct.includes('servizi') && direct.includes('pt')) return true;
  if (direct.includes('sedute') && direct.includes('pt')) return true;
  if (direct.includes('personal') && direct.includes('trainer')) return true;
  if (direct.includes('studio') && direct.includes('pt')) return true;
  return false;
};

const isPtProductName = (nameKey = '') => {
  if (!nameKey) return false;
  if (nameKey === 'pt') return true;
  if (isPtCategoryKey(nameKey)) return true;
  const safe = nameKey.replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (safe.includes('sedute pt') || safe.includes('pt sedute')) return true;
  if (/\bpt\b/.test(safe)) return true;
  return /\b(\d+\s*(sedute\s*)?pt|pt\s*\d+|pack\s*pt|pacchetti?\s*pt|servizi?\s*pt|sedute\s*pt)\b/.test(safe);
};

const normalizeCategory = (raw, line = {}) => {
  if (isPtCategoryKey(raw)) return 'pt';
  const direct = normalizeKey(raw);
  if (direct === 'adjustments') return 'adjustments';
  const nameKey = normalizeKey(resolveLineNameCandidate(line));
  if (isPtProductName(nameKey)) return 'pt';
  if (raw) return 'coaching';
  return '';
};

const resolveProductCategory = (line = {}, productIndex = createEmptyProductIndex()) => {
  if (hasPtQuantity(line)) return 'pt';
  const candidate = line.category || line.productCategory || line.type || line.group;
  const normalizedDirect = normalizeCategory(candidate, line);
  if (normalizedDirect) return normalizedDirect;

  const catalogProduct = matchCatalogProduct(line, productIndex);
  if (catalogProduct) {
    if (hasPtQuantity(catalogProduct)) return 'pt';
    const fromCatalog = normalizeCategory(
      catalogProduct.category || catalogProduct.product_category || catalogProduct.type,
      catalogProduct
    );
    if (fromCatalog) return fromCatalog;
    if (isPtProductName(normalizeKey(catalogProduct.name))) return 'pt';
  }

  if (isPtProductName(normalizeKey(resolveLineNameCandidate(line)))) return 'pt';
  return 'coaching';
};

const categoryLabel = (category) => {
  const direct = normalizeKey(category);
  if (direct === 'adjustments') return 'Differenze / Sconti / Spese';
  if (direct === 'pt') return 'Servizi PT';
  if (direct === 'coaching' || !direct) return 'Coaching';
  return formatLabel(category);
};

const resolveOrderEmail = (order = {}) => firstNonEmpty([
  order.userEmail,
  order.customerEmail,
  order.email,
  order.customer_email,
  order.billing?.email,
]);

const deriveUserDetailsFromOrder = (order = {}) => {
  const billing = order.billing || {};
  const shipping = order.shipping || {};
  const customer = order.customer || {};

  const email = firstNonEmpty([
    order.userEmail,
    customer.email,
    billing.email,
    shipping.email,
  ]);

  let name = firstNonEmpty([
    order.customerName,
    order.customerFirstName,
    order.customer_first_name,
    customer.name,
    customer.firstName,
    customer.first_name,
    billing.first_name,
    billing.firstName,
    shipping.first_name,
    shipping.firstName,
  ]);

  let surname = firstNonEmpty([
    order.customerSurname,
    order.customerLastName,
    order.customer_last_name,
    customer.surname,
    customer.lastName,
    customer.last_name,
    billing.last_name,
    billing.lastName,
    shipping.last_name,
    shipping.lastName,
  ]);

  if (!name || !surname) {
    const combined = firstNonEmpty([
      order.customerFullName,
      order.customerName,
      customer.fullName,
      customer.full_name,
      `${billing.first_name || billing.firstName || ''} ${billing.last_name || billing.lastName || ''}`,
      `${shipping.first_name || shipping.firstName || ''} ${shipping.last_name || shipping.lastName || ''}`,
    ]);
    if (combined) {
      const parts = splitFullName(combined);
      if (!name && parts.name) name = parts.name;
      if (!surname && parts.surname) surname = parts.surname;
    }
  }

  if ((!name || !surname) && email) {
    const local = email.split('@')[0] || '';
    const cleaned = local.replace(/[._-]+/g, ' ');
    const parts = cleaned.split(/\s+/).filter(Boolean);
    if (!name && parts.length) name = parts.shift();
    if (!surname && parts.length) surname = parts.join(' ');
  }

  return {
    email,
    name,
    surname,
    studio: firstNonEmpty([order.customerStudio, order.userStudio, customer.studio, billing.company, order.studio]),
    phone: firstNonEmpty([order.customerPhone, order.phone, order.userPhone, customer.phone, billing.phone, shipping.phone]),
  };
};

const resolveAmount = (order = {}) => {
  const fields = ['totalPrice', 'total', 'amount', 'grandTotal', 'orderTotal'];
  for (const field of fields) {
    const raw = order[field];
    if (raw === undefined || raw === null || raw === '') continue;
    const num = Number(raw);
    if (Number.isFinite(num)) return num;
  }
  if (Array.isArray(order.products)) {
    const sum = order.products.reduce((acc, item) => {
      const price = Number(item.price) || 0;
      const qty = Number(item.quantity) || 1;
      return acc + price * qty;
    }, 0);
    if (sum) return sum;
  }
  return 0;
};

const isInvalidStatus = (status = '') => {
  const normalized = normalizeText(status);
  if (!normalized) return false;
  return [
    'rimborsato',
    'rimborsato parzialmente',
    'cancellato',
    'scaduto',
    'refunded',
    'void',
    'expired',
    'refused',
    'annullato',
    'failed',
  ].some((token) => normalized.includes(token));
};

const isInstallmentPaidStatus = (status = '') => {
  const normalized = normalizeText(status);
  if (!normalized) return false;
  if (normalized === 'pagato' || normalized === 'paid' || normalized === 'completed') return true;
  if (normalized.includes('pagat') && !normalized.includes('attesa')) return true;
  return false;
};

const isInstallmentPaid = (installment = {}) => isInstallmentPaidStatus(installment?.status);

const installmentPaidAt = (installment = {}) => {
  return toDateSafe(
    installment?.paidAt
    || installment?.paid_at
    || installment?.paidDate
    || installment?.paid_date
  );
};

const installmentDueAt = (installment = {}) => toDateSafe(installment?.scheduledDate);

const resolveInstallmentPaidAt = (installment = {}, order = {}) => {
  if (!isInstallmentPaid(installment)) return null;
  const paidAt = installmentPaidAt(installment);
  if (paidAt) return paidAt;
  const dueAt = installmentDueAt(installment);
  if (dueAt) {
    const now = new Date();
    if (dueAt <= now) return dueAt;
  }
  const orderDate = parseOrderDate(order);
  if (orderDate) return orderDate;
  return dueAt || null;
};

const getPaidInstallments = (order = {}) => {
  const list = Array.isArray(order.manualInstallments) ? order.manualInstallments : [];
  return list.filter((installment) => {
    const amount = Number(installment?.amount);
    if (!isInstallmentPaid(installment)) return false;
    return Number.isFinite(amount) && amount > 0;
  });
};

const isManualInstallmentsOrder = (order = {}) => {
  const method = normalizeText(order.paymentMethod || order.payment_method);
  if (method === 'rate_manuali') return true;
  return Array.isArray(order.manualInstallments) && order.manualInstallments.length > 0;
};

const buildScaledLines = (order = {}, ratio = 0) => {
  const lines = safeProducts(order);
  return lines.map((line) => {
    const quantity = resolveLineQuantity(line);
    const baseAmount = resolveLineAmount(line);
    const scaledAmount = baseAmount * ratio;
    const unitPrice = quantity ? scaledAmount / quantity : scaledAmount;
    return {
      ...line,
      quantity: quantity || 1,
      price: unitPrice,
      total: scaledAmount,
      line_total: scaledAmount,
      amount: scaledAmount,
    };
  });
};

const expandManualInstallmentOrders = (order = {}) => {
  const paidPlan = getPaidInstallments(order);
  if (!paidPlan.length) return [];

  const baseTotal = resolveAmount(order);
  if (!baseTotal) return [];

  const orderId = order._id || order.orderId || order.id || 'order';

  return paidPlan
    .map((installment, index) => {
      const amount = Number(installment.amount) || 0;
      const date = resolveInstallmentPaidAt(installment, order);
      if (!amount || !date) return null;

      const ratio = amount / baseTotal;
      const scaledLines = buildScaledLines(order, ratio || 0);
      const paymentMethod = installment.method || installment.paymentMethod || order.paymentMethod || order.payment_method || 'rate_manuali';
      const installmentKey = installment.id || installment.installmentId || installment.uuid || `inst${index + 1}`;
      const pseudoId = `${orderId}#${installmentKey}`;

      return {
        ...order,
        _id: pseudoId,
        orderId,
        orderDate: date.toISOString(),
        paymentMethod,
        paymentStatus: 'paid',
        status: 'pagato',
        manualInstallments: [installment],
        totalPrice: amount,
        amount,
        products: scaledLines,
      };
    })
    .filter(Boolean);
};

const isOrderPaid = (order = {}) => {
  const statusRaw = normalizeText(order.status || order.orderStatus);
  const paymentStatus = normalizeText(order.paymentStatus || order.payment_status);
  const method = normalizeText(order.paymentMethod || order.payment_method);

  const blockedStatuses = new Set([
    'cancelled',
    'wc-cancelled',
    'refunded',
    'wc-refunded',
    'pending',
    'wc-pending',
    'on-hold',
    'wc-on-hold',
    'cancellato',
    'annullato',
    'in attesa di pagamento',
    'attesa di pagamento',
    'pagamento in attesa',
    'rimborsato',
    'rimborsato completamente',
  ]);

  if (blockedStatuses.has(statusRaw)) return false;
  if (statusRaw.includes('cancelled') || statusRaw.includes('refunded')) return false;
  if (statusRaw.includes('cancellat') || statusRaw.includes('annullat')) return false;
  if (statusRaw.includes('rimborsat') || statusRaw.includes('rimborso')) return false;
  if (statusRaw.includes('attesa') && statusRaw.includes('pagament')) return false;
  if (statusRaw.includes('pending') && !statusRaw.includes('completed')) return false;

  const paidStatuses = new Set([
    'pagato',
    'rateizzato',
    'paid',
    'completed',
    'complete',
    'processing',
    'wc-completed',
    'wc-processing',
    'direct_purchase',
    'payment_completed',
    'completed_payment',
  ]);

  if (paidStatuses.has(statusRaw)) return true;
  if (statusRaw.startsWith('wc-') && (statusRaw.includes('completed') || statusRaw.includes('processing'))) return true;
  if (statusRaw.includes('direct') && statusRaw.includes('purchase')) return true;
  if (statusRaw.includes('payment') && statusRaw.includes('complete')) return true;

  const paidPaymentStatuses = new Set(['paid', 'completed', 'complete', 'captured', 'succeeded', 'settled']);
  if (paymentStatus.includes('rimborsat') || paymentStatus.includes('rimborso')) return false;
  if (paymentStatus.includes('attesa') && paymentStatus.includes('pagament')) return false;
  if (paidPaymentStatuses.has(paymentStatus)) return true;
  if (paymentStatus.includes('completed') || paymentStatus.includes('captured')) return true;

  if (method === 'contanti' || method === 'cash' || method === 'custom_cash') return true;
  return false;
};

const allocateRevenueByOrderTotal = (grossTotals = [], orderTotalValue = null) => {
  const grossCents = grossTotals.map((value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed * 100)) : 0;
  });

  const sumGross = grossCents.reduce((sum, value) => sum + value, 0);
  const orderTotal = Number(orderTotalValue);
  const targetTotal = Number.isFinite(orderTotal) ? Math.max(0, Math.round(orderTotal * 100)) : null;

  if (targetTotal === null || sumGross <= 0) {
    return grossCents.map((value) => value / 100);
  }

  if (grossCents.length === 1) {
    return [targetTotal / 100];
  }

  let allocated = 0;
  return grossCents.map((value, index) => {
    if (index === grossCents.length - 1) {
      return (targetTotal - allocated) / 100;
    }
    const slice = Math.round((value / sumGross) * targetTotal);
    allocated += slice;
    return slice / 100;
  });
};

export default {
  name: 'OrdersAnalytics',
  components: {
    SidebarComponent,
  },
  setup() {
    const db = getFirestore();
    const { from, to } = defaultDateRange();

    const loading = ref(false);
    const hasLoaded = ref(false);
    const error = ref('');
    const progressText = ref('');
    const lastUpdatedAt = ref(null);
    const totalOrdersRead = ref(0);
    const rawOrders = ref([]);
    const productIndex = ref(createEmptyProductIndex());
    const productsLoaded = ref(false);

    const filters = reactive({
      fromDate: from,
      toDate: to,
      source: '',
      statusMode: 'pagati',
      categoryMode: 'coaching',
      product: '',
    });

    const selectedProductKey = ref('');
    const selectedMonthKey = ref('all');
    const accordions = reactive({
      overview: false,
      trend: false,
      focus: false,
      comparison: false,
      detail: false,
    });

    const sourceOptions = [
      { value: 'woocommerce', label: 'WooCommerce' },
      { value: 'legacy', label: 'Legacy' },
      { value: 'manuale', label: 'Manuale' },
      { value: 'importato', label: 'Importato' },
    ];

    const statusModeOptions = [
      { value: 'pagati', label: 'Solo ordini pagati / processing' },
      { value: 'attivi', label: 'Solo ordini attivi' },
      { value: 'tutti', label: 'Tutti gli ordini' },
    ];

    const categoryOptions = [
      { value: 'coaching', label: 'Solo Coaching' },
      { value: 'all', label: 'Tutte le categorie' },
      { value: 'pt', label: 'Solo Servizi PT' },
    ];

    const canLoad = computed(() => !!filters.fromDate && !!filters.toDate && !loading.value);

    const formatCurrency = (value) => {
      const amount = Number(value) || 0;
      return amount.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
    };

    const formatNumber = (value) => {
      const amount = Number(value) || 0;
      return amount.toLocaleString('it-IT');
    };

    const formatPercent = (value) => {
      const amount = Number(value) || 0;
      return `${amount.toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
    };

    const formatDateTime = (value) => {
      const parsed = toDateSafe(value);
      if (!parsed) return '—';
      return parsed.toLocaleString('it-IT');
    };

    const ratioPercent = (value, max) => {
      const safeValue = Number(value) || 0;
      const safeMax = Number(max) || 0;
      if (safeValue <= 0 || safeMax <= 0) return 0;
      return Math.max(4, Math.min(100, (safeValue / safeMax) * 100));
    };

    const buildOrderDateConstraints = ({ startDate, endDate, cursor = null }) => {
      const constraints = [];
      if (startDate) constraints.push(where('orderDate', '>=', composeOrderDateBoundary(startDate, 'start')));
      if (endDate) constraints.push(where('orderDate', '<=', composeOrderDateBoundary(endDate, 'end')));
      constraints.push(orderBy('orderDate', 'desc'));
      if (cursor) constraints.push(startAfter(cursor));
      constraints.push(limit(ORDER_PAGE_SIZE));
      return constraints;
    };

    const buildImportedConstraints = ({ startDate, endDate, cursor = null }) => {
      const constraints = [];
      if (startDate) constraints.push(where('importedAt', '>=', composeImportedBoundary(startDate, 'start')));
      if (endDate) constraints.push(where('importedAt', '<=', composeImportedBoundary(endDate, 'end')));
      constraints.push(orderBy('importedAt', 'desc'));
      if (cursor) constraints.push(startAfter(cursor));
      constraints.push(limit(ORDER_PAGE_SIZE));
      return constraints;
    };

    const fetchPagedDocs = async (buildConstraints, progressLabel) => {
      const docs = [];
      let cursor = null;
      let page = 0;
      let keepGoing = true;

      while (keepGoing) {
        page += 1;
        // eslint-disable-next-line no-await-in-loop
        const snap = await getDocs(query(collection(db, 'orders'), ...buildConstraints(cursor)));
        if (snap.empty) break;
        docs.push(...snap.docs);
        progressText.value = `${progressLabel}: pagina ${page}, ${formatNumber(docs.length)} documenti letti...`;
        cursor = snap.docs[snap.docs.length - 1];
        if (snap.size < ORDER_PAGE_SIZE) {
          keepGoing = false;
        }
      }

      return docs;
    };

    const loadProducts = async () => {
      if (productsLoaded.value) return;
      try {
        const snap = await getDocs(query(collection(db, 'products')));
        const products = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
        productIndex.value = buildProductIndex(products);
        productsLoaded.value = true;
      } catch (err) {
        console.warn('OrdersAnalytics: impossibile caricare products', err);
        productIndex.value = createEmptyProductIndex();
      }
    };

    const buildLinesFromOrder = (docId, order, collector) => {
      const analyticsOrderKey = firstNonEmpty([order._id, docId, order.orderId, order.id]) || `order_${collector.length}`;
      const orderDate = parseOrderDate(order);
      const orderDateMs = orderDate ? orderDate.getTime() : 0;
      const orderId = firstNonEmpty([order.orderId, docId, order.id, analyticsOrderKey]) || '—';
      const orderStatus = firstNonEmpty([order.status, order.orderStatus, order.statusOrder]);
      const paymentStatus = firstNonEmpty([order.paymentStatus, order.payment_state, order.paymentState, order.payment_status]);
      const source = normalizeSource(order, analyticsOrderKey);
      const email = resolveOrderEmail(order);
      const customer = deriveUserDetailsFromOrder(order);

      let products = safeProducts(order);
      const orderAmount = resolveAmount(order);

      if (!products.length) {
        products = [{
          name: firstNonEmpty([
            order.productName,
            order.product_title,
            order.productTitle,
            order.name,
            order.title,
          ]) || 'Senza nome',
          quantity: order.quantity || order.qty || 1,
          total: orderAmount,
          price: orderAmount || order.price || 0,
        }];
      }

      const normalizedLines = products.map((line, index) => {
        const catalogProduct = matchCatalogProduct(line, productIndex.value);
        const quantity = resolveLineQuantity(line);
        const unitPrice = resolveLineUnitPrice(line);
        const grossTotal = resolveLineTotal(line, quantity, unitPrice);
        const category = resolveProductCategory(line, productIndex.value);
        return {
          index,
          productName: resolveLineDisplayName(line, catalogProduct),
          quantity,
          unitPrice,
          grossTotal,
          sku: resolveLineSku(line),
          productId: resolveLineProductId(line),
          category,
        };
      });

      const netTotals = allocateRevenueByOrderTotal(
        normalizedLines.map((line) => line.grossTotal),
        orderAmount
      );

      normalizedLines.forEach((line, index) => {
        collector.push({
          key: `${analyticsOrderKey}_${index}_${line.sku || line.productId || line.productName}`,
          orderDocId: analyticsOrderKey,
          orderBaseDocId: docId,
          orderId,
          orderDateMs,
          orderStatus,
          paymentStatus,
          source,
          email,
          customerName: customer.name,
          customerSurname: customer.surname,
          productName: line.productName,
          category: line.category,
          categoryLabel: categoryLabel(line.category),
          quantity: line.quantity,
          unitPrice: line.unitPrice,
          grossAmount: line.grossTotal,
          revenue: Number(netTotals[index] || 0),
          sku: line.sku,
          productId: line.productId,
        });
      });
    };

    const rawLines = computed(() => {
      const collector = [];

      rawOrders.value.forEach((order) => {
        const docId = order._id || order.orderId || order.id || '';

        if (filters.statusMode === 'pagati') {
          if (isManualInstallmentsOrder(order)) {
            expandManualInstallmentOrders(order).forEach((part) => {
              if (!isWithinDate(part, filters.fromDate, filters.toDate)) return;
              buildLinesFromOrder(docId, part, collector);
            });
            return;
          }
          if (!isOrderPaid(order)) return;
          if (!isWithinDate(order, filters.fromDate, filters.toDate)) return;
          buildLinesFromOrder(docId, order, collector);
          return;
        }

        if (!isWithinDate(order, filters.fromDate, filters.toDate)) return;

        if (filters.statusMode === 'attivi') {
          const invalid = isInvalidStatus(firstNonEmpty([order.status, order.orderStatus, order.statusOrder]))
            || isInvalidStatus(firstNonEmpty([order.paymentStatus, order.payment_state, order.paymentState, order.payment_status]));
          if (invalid) return;
        }

        buildLinesFromOrder(docId, order, collector);
      });

      return collector
        .slice()
        .sort((a, b) => (b.orderDateMs || 0) - (a.orderDateMs || 0));
    });

    const loadOrders = async () => {
      if (loading.value) return;
      if (!filters.fromDate || !filters.toDate) {
        error.value = 'Seleziona sia la data iniziale sia la data finale.';
        hasLoaded.value = false;
        return;
      }

      const startDate = new Date(`${filters.fromDate}T00:00:00`);
      const endDate = new Date(`${filters.toDate}T23:59:59`);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        error.value = 'Intervallo date non valido.';
        hasLoaded.value = false;
        return;
      }
      if (startDate.getTime() > endDate.getTime()) {
        error.value = 'La data iniziale non puo essere successiva alla data finale.';
        hasLoaded.value = false;
        return;
      }

      loading.value = true;
      error.value = '';
      progressText.value = 'Caricamento ordini del periodo...';

      try {
        await loadProducts();
        const docsList = [];
        let orderDateQueryFailed = false;

        try {
          const orderDateDocs = await fetchPagedDocs(
            (cursor) => buildOrderDateConstraints({
              startDate: filters.fromDate,
              endDate: filters.toDate,
              cursor,
            }),
            'Query orderDate'
          );
          docsList.push(...orderDateDocs);
        } catch (primaryErr) {
          orderDateQueryFailed = true;
          console.warn('OrdersAnalytics: query per orderDate fallita, uso fallback su importedAt', primaryErr);
          progressText.value = 'Query orderDate non disponibile, fallback su importedAt...';
          try {
            const fallbackDocs = await fetchPagedDocs(
              (cursor) => buildImportedConstraints({
                startDate: filters.fromDate,
                endDate: filters.toDate,
                cursor,
              }),
              'Query importedAt'
            );
            docsList.push(...fallbackDocs);
          } catch (fallbackErr) {
            console.error('OrdersAnalytics: anche il fallback su importedAt e fallito', fallbackErr);
            error.value = 'Errore durante il caricamento degli ordini.';
          }
        }

        if (!orderDateQueryFailed) {
          try {
            const supplementalDocs = await fetchPagedDocs(
              (cursor) => buildImportedConstraints({
                startDate: filters.fromDate,
                endDate: filters.toDate,
                cursor,
              }),
              'Integrazione importedAt'
            );
            docsList.push(...supplementalDocs);
          } catch (supplementalErr) {
            console.warn('OrdersAnalytics: query supplementare su importedAt fallita', supplementalErr);
          }
        }

        try {
          const manualSnap = await getDocs(query(collection(db, 'orders'), where('paymentMethod', '==', 'rate_manuali')));
          docsList.push(...manualSnap.docs);
        } catch (manualErr) {
          console.warn('OrdersAnalytics: query su rate_manuali fallita', manualErr);
        }

        const seen = new Set();
        const results = [];
        docsList.forEach((docSnap) => {
          if (seen.has(docSnap.id)) return;
          seen.add(docSnap.id);
          results.push({ _id: docSnap.id, ...docSnap.data() });
        });

        rawOrders.value = results.filter((order) => {
          return isManualInstallmentsOrder(order) || isWithinDate(order, filters.fromDate, filters.toDate);
        });
        totalOrdersRead.value = rawOrders.value.length;
        lastUpdatedAt.value = new Date();
        hasLoaded.value = true;
        progressText.value = `Dataset aggiornato: ${formatNumber(rawOrders.value.length)} ordini letti nel range ${filters.fromDate} - ${filters.toDate}.`;
      } catch (err) {
        console.error('OrdersAnalytics loadOrders error:', err);
        error.value = 'Errore durante il caricamento degli ordini.';
        hasLoaded.value = false;
        progressText.value = '';
      } finally {
        loading.value = false;
      }
    };

    const resetFilters = () => {
      const range = defaultDateRange();
      filters.fromDate = range.from;
      filters.toDate = range.to;
      filters.source = '';
      filters.statusMode = 'pagati';
      filters.categoryMode = 'coaching';
      filters.product = '';
      rawOrders.value = [];
      totalOrdersRead.value = 0;
      lastUpdatedAt.value = null;
      error.value = '';
      progressText.value = '';
      hasLoaded.value = false;
      selectedMonthKey.value = 'all';
    };

    const filteredLines = computed(() => {
      const productNeedle = normalizeText(filters.product);
      const sourceNeedle = normalizeText(filters.source);
      const categoryNeedle = normalizeText(filters.categoryMode);

      return rawLines.value.filter((line) => {
        if (sourceNeedle && normalizeText(line.source) !== sourceNeedle) return false;
        if (categoryNeedle && categoryNeedle !== 'all' && normalizeText(line.category) !== categoryNeedle) return false;
        if (productNeedle && !normalizeText(`${line.productName} ${line.sku} ${line.productId}`).includes(productNeedle)) {
          return false;
        }
        return true;
      });
    });

    const analytics = computed(() => {
      const ordersSet = new Set();
      const sourceMap = new Map();
      const productMap = new Map();
      const monthMap = new Map();

      let revenue = 0;
      let quantity = 0;

      filteredLines.value.forEach((line) => {
        if (line.orderDocId) ordersSet.add(line.orderDocId);

        const lineRevenue = Number(line.revenue) || 0;
        const lineQuantity = Number(line.quantity) || 0;
        revenue += lineRevenue;
        quantity += lineQuantity;

        const sourceKey = normalizeSource({ source: line.source }, line.source) || line.source || 'woocommerce';
        let sourceEntry = sourceMap.get(sourceKey);
        if (!sourceEntry) {
          sourceEntry = {
            source: sourceKey,
            label: sourceLabel(sourceKey),
            revenue: 0,
            quantity: 0,
            orders: new Set(),
          };
          sourceMap.set(sourceKey, sourceEntry);
        }
        sourceEntry.revenue += lineRevenue;
        sourceEntry.quantity += lineQuantity;
        if (line.orderDocId) sourceEntry.orders.add(line.orderDocId);

        const monthKey = monthKeyFromMs(line.orderDateMs);
        if (monthKey) {
          let monthEntry = monthMap.get(monthKey);
          if (!monthEntry) {
            monthEntry = {
              key: monthKey,
              label: monthLabelFromKey(monthKey),
              revenue: 0,
              quantity: 0,
              orders: new Set(),
            };
            monthMap.set(monthKey, monthEntry);
          }
          monthEntry.revenue += lineRevenue;
          monthEntry.quantity += lineQuantity;
          if (line.orderDocId) monthEntry.orders.add(line.orderDocId);
        }

        const productKey = normalizeText(line.productName) ? `name:${normalizeText(line.productName)}` : 'name:senza_nome';
        let productEntry = productMap.get(productKey);
        if (!productEntry) {
          productEntry = {
            key: productKey,
            productName: line.productName || 'Senza nome',
            revenue: 0,
            quantity: 0,
            orders: new Set(),
            sources: new Set(),
            skuSet: new Set(),
            productIdSet: new Set(),
            firstOrderMs: 0,
            lastOrderMs: 0,
            months: new Map(),
          };
          productMap.set(productKey, productEntry);
        }

        productEntry.revenue += lineRevenue;
        productEntry.quantity += lineQuantity;
        if (line.orderDocId) productEntry.orders.add(line.orderDocId);
        if (line.source) productEntry.sources.add(line.source);
        if (line.sku) productEntry.skuSet.add(line.sku);
        if (line.productId) productEntry.productIdSet.add(line.productId);
        if (line.orderDateMs) {
          if (!productEntry.firstOrderMs || line.orderDateMs < productEntry.firstOrderMs) {
            productEntry.firstOrderMs = line.orderDateMs;
          }
          if (!productEntry.lastOrderMs || line.orderDateMs > productEntry.lastOrderMs) {
            productEntry.lastOrderMs = line.orderDateMs;
          }
        }

        if (monthKey) {
          let productMonth = productEntry.months.get(monthKey);
          if (!productMonth) {
            productMonth = {
              key: monthKey,
              label: monthLabelFromKey(monthKey),
              revenue: 0,
              quantity: 0,
              orders: new Set(),
            };
            productEntry.months.set(monthKey, productMonth);
          }
          productMonth.revenue += lineRevenue;
          productMonth.quantity += lineQuantity;
          if (line.orderDocId) productMonth.orders.add(line.orderDocId);
        }
      });

      const productRows = Array.from(productMap.values())
        .map((entry) => {
          const months = Array.from(entry.months.values())
            .map((month) => ({
              key: month.key,
              label: month.label,
              revenue: roundCurrency(month.revenue),
              quantity: month.quantity,
              orders: month.orders.size,
            }))
            .sort((a, b) => a.key.localeCompare(b.key));

          return {
            key: entry.key,
            productName: entry.productName,
            revenue: roundCurrency(entry.revenue),
            quantity: entry.quantity,
            orderCount: entry.orders.size,
            sources: Array.from(entry.sources).sort(),
            skuLabel: entry.skuSet.size === 1
              ? Array.from(entry.skuSet)[0]
              : (entry.productIdSet.size === 1 ? Array.from(entry.productIdSet)[0] : (entry.skuSet.size || entry.productIdSet.size ? 'varie' : '—')),
            firstOrderLabel: entry.firstOrderMs ? formatDateTime(entry.firstOrderMs) : '—',
            lastOrderLabel: entry.lastOrderMs ? formatDateTime(entry.lastOrderMs) : '—',
            lastOrderMs: entry.lastOrderMs,
            months,
          };
        })
        .sort((a, b) => {
          if (b.revenue !== a.revenue) return b.revenue - a.revenue;
          if (b.quantity !== a.quantity) return b.quantity - a.quantity;
          return a.productName.localeCompare(b.productName, 'it');
        });

      const monthlyTrend = Array.from(monthMap.values())
        .map((entry) => ({
          key: entry.key,
          label: entry.label,
          revenue: roundCurrency(entry.revenue),
          quantity: entry.quantity,
          orders: entry.orders.size,
        }))
        .sort((a, b) => a.key.localeCompare(b.key));

      const sourceSummary = Array.from(sourceMap.values())
        .map((entry) => ({
          source: entry.source,
          label: entry.label,
          revenue: roundCurrency(entry.revenue),
          quantity: entry.quantity,
          orders: entry.orders.size,
          revenueShare: revenue > 0 ? (entry.revenue / revenue) * 100 : 0,
        }))
        .sort((a, b) => b.revenue - a.revenue);

      return {
        revenue: roundCurrency(revenue),
        quantity,
        orders: ordersSet.size,
        uniqueProducts: productRows.length,
        avgOrderValue: ordersSet.size > 0 ? revenue / ordersSet.size : 0,
        productRows,
        monthlyTrend,
        sourceSummary,
      };
    });

    const summary = computed(() => ({
      revenue: analytics.value.revenue,
      quantity: analytics.value.quantity,
      orders: analytics.value.orders,
      uniqueProducts: analytics.value.uniqueProducts,
      avgOrderValue: analytics.value.avgOrderValue,
    }));

    const productRows = computed(() => analytics.value.productRows);
    const sourceSummary = computed(() => analytics.value.sourceSummary);
    const monthlyTrend = computed(() => analytics.value.monthlyTrend);

    const topRevenueProducts = computed(() => productRows.value.slice(0, TOP_PRODUCTS_LIMIT));
    const topQuantityProducts = computed(() => {
      return productRows.value
        .slice()
        .sort((a, b) => {
          if (b.quantity !== a.quantity) return b.quantity - a.quantity;
          return b.revenue - a.revenue;
        })
        .slice(0, TOP_PRODUCTS_LIMIT);
    });

    const maxRevenueValue = computed(() => Math.max(...topRevenueProducts.value.map((item) => item.revenue), 0));
    const maxQuantityValue = computed(() => Math.max(...topQuantityProducts.value.map((item) => item.quantity), 0));
    const maxMonthRevenue = computed(() => Math.max(...monthlyTrend.value.map((item) => item.revenue), 0));
    const maxMonthQuantity = computed(() => Math.max(...monthlyTrend.value.map((item) => item.quantity), 0));

    const selectedProduct = computed(() => {
      if (!productRows.value.length) return null;
      return productRows.value.find((product) => product.key === selectedProductKey.value) || productRows.value[0];
    });

    const selectedProductMonths = computed(() => {
      if (!selectedProduct.value) return [];
      const rangeRevenue = analytics.value.revenue || 0;
      return selectedProduct.value.months
        .map((month) => ({
          ...month,
          revenueShare: rangeRevenue > 0 ? (month.revenue / rangeRevenue) * 100 : 0,
        }))
        .sort((a, b) => b.key.localeCompare(a.key));
    });

    const comparisonMonths = computed(() => {
      return monthlyTrend.value.map((month) => ({
        key: month.key,
        label: month.label,
        shortLabel: monthShortLabelFromKey(month.key),
      }));
    });

    const productComparisonRows = computed(() => {
      return productRows.value.map((product) => {
        const monthMap = new Map(product.months.map((month) => [month.key, month]));
        return {
          ...product,
          monthCells: comparisonMonths.value.map((month) => {
            const monthEntry = monthMap.get(month.key);
            return {
              key: month.key,
              label: month.label,
              shortLabel: month.shortLabel,
              quantity: monthEntry?.quantity || 0,
              revenue: monthEntry?.revenue || 0,
              orders: monthEntry?.orders || 0,
            };
          }),
        };
      });
    });

    const comparisonVisualRows = computed(() => {
      const ranked = productComparisonRows.value
        .slice()
        .sort((a, b) => {
          if (b.quantity !== a.quantity) return b.quantity - a.quantity;
          if (b.revenue !== a.revenue) return b.revenue - a.revenue;
          return a.productName.localeCompare(b.productName, 'it');
        });

      const rows = [];
      const selectedRow = ranked.find((product) => product.key === selectedProductKey.value);
      if (selectedRow) rows.push(selectedRow);

      for (const row of ranked) {
        if (rows.some((entry) => entry.key === row.key)) continue;
        rows.push(row);
        if (rows.length >= COMPARISON_VISUAL_LIMIT) break;
      }

      return rows;
    });

    const maxComparisonMonthQuantity = computed(() => {
      return Math.max(
        ...comparisonVisualRows.value.flatMap((product) => product.monthCells.map((cell) => cell.quantity)),
        0
      );
    });

    const comparisonChartViewBox = computed(() => `0 0 ${COMPARISON_CHART_WIDTH} ${COMPARISON_CHART_HEIGHT}`);
    const comparisonChartBottomY = computed(() => COMPARISON_CHART_HEIGHT - COMPARISON_CHART_PADDING.bottom);
    const comparisonChartMonths = computed(() => {
      const plotWidth = COMPARISON_CHART_WIDTH - COMPARISON_CHART_PADDING.left - COMPARISON_CHART_PADDING.right;
      const count = comparisonMonths.value.length;

      return comparisonMonths.value.map((month, index) => ({
        ...month,
        x: count <= 1
          ? COMPARISON_CHART_PADDING.left + (plotWidth / 2)
          : COMPARISON_CHART_PADDING.left + ((plotWidth * index) / (count - 1)),
      }));
    });

    const comparisonChartTicks = computed(() => {
      const safeMax = Math.max(maxComparisonMonthQuantity.value, 1);
      const plotHeight = COMPARISON_CHART_HEIGHT - COMPARISON_CHART_PADDING.top - COMPARISON_CHART_PADDING.bottom;
      return Array.from({ length: 5 }, (_, index) => {
        const ratio = index / 4;
        const value = Math.round(safeMax * (1 - ratio));
        return {
          value,
          y: COMPARISON_CHART_PADDING.top + (plotHeight * ratio),
        };
      });
    });

    const comparisonLineSeries = computed(() => {
      const safeMax = Math.max(maxComparisonMonthQuantity.value, 1);
      const plotHeight = COMPARISON_CHART_HEIGHT - COMPARISON_CHART_PADDING.top - COMPARISON_CHART_PADDING.bottom;

      return comparisonVisualRows.value.map((product, index) => {
        const color = COMPARISON_LINE_COLORS[index % COMPARISON_LINE_COLORS.length];
        const points = comparisonChartMonths.value.map((month) => {
          const cell = product.monthCells.find((item) => item.key === month.key) || {
            key: month.key,
            quantity: 0,
            revenue: 0,
            orders: 0,
            label: month.label,
            shortLabel: month.shortLabel,
          };
          const quantity = Number(cell.quantity) || 0;
          const y = COMPARISON_CHART_PADDING.top + plotHeight - ((quantity / safeMax) * plotHeight);
          return {
            ...cell,
            x: month.x,
            y,
          };
        });

        return {
          ...product,
          color,
          points,
          path: points.map((point, pointIndex) => `${pointIndex === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' '),
        };
      });
    });

    const focusStats = computed(() => {
      if (!selectedProduct.value) {
        return {
          quantity: 0,
          orders: 0,
          revenue: 0,
          scopeLabel: 'Nessun prodotto selezionato',
        };
      }

      if (selectedMonthKey.value === 'all') {
        return {
          quantity: selectedProduct.value.quantity,
          orders: selectedProduct.value.orderCount,
          revenue: selectedProduct.value.revenue,
          scopeLabel: 'Intero intervallo selezionato',
        };
      }

      const month = selectedProductMonths.value.find((entry) => entry.key === selectedMonthKey.value);
      if (!month) {
        return {
          quantity: selectedProduct.value.quantity,
          orders: selectedProduct.value.orderCount,
          revenue: selectedProduct.value.revenue,
          scopeLabel: 'Intero intervallo selezionato',
        };
      }

      return {
        quantity: month.quantity,
        orders: month.orders,
        revenue: month.revenue,
        scopeLabel: month.label,
      };
    });

    watch(productRows, (rows) => {
      if (!rows.length) {
        selectedProductKey.value = '';
        selectedMonthKey.value = 'all';
        return;
      }
      if (!rows.some((product) => product.key === selectedProductKey.value)) {
        selectedProductKey.value = rows[0].key;
      }
    }, { immediate: true });

    watch(selectedProduct, (product) => {
      if (!product) {
        selectedMonthKey.value = 'all';
        return;
      }
      if (selectedMonthKey.value === 'all') return;
      const exists = product.months.some((month) => month.key === selectedMonthKey.value);
      if (!exists) {
        selectedMonthKey.value = 'all';
      }
    }, { immediate: true });

    onMounted(() => {
      resetFilters();
    });

    return {
      accordions,
      COMPARISON_CHART_HEIGHT,
      COMPARISON_CHART_WIDTH,
      canLoad,
      comparisonChartBottomY,
      comparisonChartMonths,
      comparisonChartTicks,
      comparisonChartViewBox,
      comparisonLineSeries,
      error,
      filters,
      focusStats,
      formatCurrency,
      formatDateTime,
      formatNumber,
      formatPercent,
      hasLoaded,
      lastUpdatedAt,
      loadOrders,
      loading,
      maxMonthQuantity,
      maxMonthRevenue,
      maxQuantityValue,
      maxRevenueValue,
      monthlyTrend,
      comparisonMonths,
      comparisonVisualRows,
      maxComparisonMonthQuantity,
      productRows,
      productComparisonRows,
      progressText,
      ratioPercent,
      rawLines,
      resetFilters,
      selectedMonthKey,
      selectedProduct,
      selectedProductKey,
      selectedProductMonths,
      sourceLabel,
      sourceOptions,
      categoryOptions,
      sourceSummary,
      statusModeOptions,
      summary,
      topQuantityProducts,
      topRevenueProducts,
      totalOrdersRead,
    };
  },
};
</script>

<style scoped>
.analytics-page {
  padding-bottom: 32px;
}

.analytics-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 18px;
  flex-wrap: wrap;
}

.subtitle {
  margin-top: 8px;
  color: #64748b;
  max-width: 760px;
}

.hero-badge {
  min-width: 220px;
  padding: 18px 20px;
  border-radius: 22px;
  background: linear-gradient(145deg, #0f172a, #1d4ed8);
  color: #ffffff;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.22);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.hero-badge__value {
  font-size: 1.6rem;
  font-weight: 800;
}

.hero-badge__label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.82);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.card-surface {
  background: #ffffff;
  border-radius: 22px;
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.08);
  padding: 22px 24px;
  margin-top: 18px;
}

.accordion {
  overflow: hidden;
}

.accordion__summary {
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  cursor: pointer;
  text-align: left;
}

.accordion__heading {
  flex: 1;
  text-align: center;
}

.accordion__heading h2 {
  margin: 0;
  color: #0f172a;
}

.accordion__heading p {
  margin: 6px auto 0;
  color: #64748b;
  max-width: 820px;
}

.accordion__icon {
  flex: none;
  color: #475569;
  font-size: 28px;
  transition: transform 0.18s ease;
}

.accordion__icon--open {
  transform: rotate(180deg);
}

.accordion__content {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid rgba(148, 163, 184, 0.16);
}

.filters-card {
  margin-top: 26px;
  border: 1px solid rgba(148, 163, 184, 0.16);
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(16, minmax(0, 1fr));
  gap: 16px;
  align-items: end;
}

.field-group {
  grid-column: span 2;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-group--wide {
  grid-column: span 4;
}

.field-group--actions {
  grid-column: span 2;
}

.field-group label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
}

.field-group input,
.field-group select {
  width: 77%;
  min-height: 24px;
  border-radius: 14px;
  border: 1px solid #dbe3ef;
  background: #f8fafc;
  color: #0f172a;
  padding: 10px 14px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.field-group input:focus,
.field-group select:focus {
  outline: none;
  border-color: rgba(37, 99, 235, 0.7);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
  background: #ffffff;
}

.filters-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-start;
  align-items: center;
  min-height: 44px;
}

.filters-note {
  margin-top: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  color: #64748b;
  font-size: 0.86rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 14px;
  border: 1px solid transparent;
  font-weight: 700;
  font-size: 0.92rem;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.14);
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn.primary {
  background: linear-gradient(135deg, #2563eb, #0ea5e9);
  color: #ffffff;
}

.btn.ghost {
  background: #eef4ff;
  color: #1e3a8a;
}

.btn--icon {
  width: 46px;
  min-width: 46px;
  height: 46px;
  padding: 0;
  border-radius: 14px;
}

.btn--icon .material-icons {
  font-size: 20px;
}

.icon-spin {
  animation: icon-spin 0.95s linear infinite;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
  margin-top: 18px;
}

.kpi-card {
  border-radius: 22px;
  padding: 20px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(248, 250, 252, 0.98)),
    radial-gradient(circle at top right, rgba(14, 165, 233, 0.14), transparent 55%);
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.kpi-card__label {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  color: #64748b;
}

.kpi-card__value {
  font-size: 1.45rem;
  line-height: 1.1;
  color: #0f172a;
}

.kpi-card__meta {
  color: #64748b;
  font-size: 0.82rem;
}

.section-heading {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 18px;
  text-align: center;
}

.section-heading > div {
  width: 100%;
}

.section-heading h2 {
  margin: 0;
  color: #0f172a;
}

.section-heading p {
  margin: 6px auto 0;
  color: #64748b;
  max-width: 820px;
}

.source-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.source-card {
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  padding: 16px 18px;
  background: #f8fafc;
}

.source-card__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.source-card__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #475569;
  font-size: 0.88rem;
}

.chart-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin-top: 18px;
  align-items: start;
}

.chart-card {
  min-height: 0;
  height: fit-content;
  align-self: start;
}

.chart-grid > .card-surface {
  margin-top: 0;
}

.bar-chart-list,
.month-chart-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.bar-chart-item,
.month-chart-row {
  border-radius: 18px;
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.16);
  padding: 14px 16px;
}

.bar-chart-item__head,
.month-bar-block__meta,
.month-chart-row__label {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.bar-chart-item__head small,
.month-chart-row__label small {
  display: block;
  margin-top: 4px;
  color: #64748b;
}

.bar-track {
  margin-top: 10px;
  position: relative;
  width: 100%;
  height: 12px;
  border-radius: 999px;
  overflow: hidden;
  background: linear-gradient(90deg, rgba(148, 163, 184, 0.18), rgba(148, 163, 184, 0.1));
}

.bar-track--soft {
  background: linear-gradient(90deg, rgba(14, 165, 233, 0.1), rgba(14, 165, 233, 0.04));
}

.bar-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
}

.bar-fill--revenue {
  background: linear-gradient(90deg, #1d4ed8, #38bdf8);
}

.bar-fill--quantity {
  background: linear-gradient(90deg, #16a34a, #86efac);
}

.month-chart-row {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 18px;
  align-items: center;
}

.month-chart-row__bars {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.month-bar-block__meta {
  margin-bottom: 8px;
  color: #334155;
  font-size: 0.88rem;
}

.trend-section {
  position: relative;
  z-index: 1;
  margin-top: 26px;
  overflow: hidden;
}

.trend-section .section-heading {
  display: block;
  margin-bottom: 22px;
}

.trend-section .section-heading h2 {
  display: block;
  margin: 0 0 8px;
}

.focus-card {
  margin-top: 18px;
}

.comparison-card {
  margin-top: 18px;
}

.comparison-chart-shell {
  padding: 10px 0 0;
}

.comparison-chart {
  display: block;
  width: 100%;
  min-height: 320px;
}

.comparison-chart__grid {
  stroke: rgba(148, 163, 184, 0.22);
  stroke-width: 1;
}

.comparison-chart__axis,
.comparison-chart__guide {
  stroke: rgba(148, 163, 184, 0.18);
  stroke-width: 1;
}

.comparison-chart__guide {
  stroke-dasharray: 4 6;
}

.comparison-chart__line {
  fill: none;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.comparison-chart__point {
  stroke: #ffffff;
  stroke-width: 2.5;
}

.comparison-chart__tick {
  fill: #64748b;
  font-size: 11px;
  text-anchor: end;
}

.comparison-chart__label {
  fill: #475569;
  font-size: 12px;
  text-anchor: middle;
}

.comparison-legend {
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.comparison-legend__item {
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: #f8fafc;
  border-radius: 18px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  text-align: left;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.comparison-legend__item:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
}

.comparison-legend__item--active {
  border-color: rgba(37, 99, 235, 0.35);
  background: #eef4ff;
}

.comparison-legend__swatch {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  flex: none;
}

.comparison-legend__copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.comparison-legend__copy strong {
  color: #0f172a;
  font-size: 0.95rem;
}

.comparison-legend__copy small {
  color: #64748b;
}

.focus-filters {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
  align-items: end;
  margin-bottom: 18px;
}

.focus-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.focus-stat {
  padding: 16px 18px;
  border-radius: 18px;
  background: linear-gradient(145deg, #eff6ff, #f8fafc);
  border: 1px solid rgba(96, 165, 250, 0.16);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.focus-stat span {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  color: #64748b;
}

.focus-stat strong {
  font-size: 1.2rem;
  color: #0f172a;
}

.focus-stat small {
  color: #64748b;
}

.focus-month-table-wrap {
  overflow-x: auto;
}

.table-wrap {
  overflow-x: auto;
}

.products-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
}

.products-table th {
  text-align: left;
  padding: 12px 14px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  background: #f8fafc;
}

.products-table td {
  padding: 14px;
  border-bottom: 1px solid #e2e8f0;
  color: #0f172a;
  vertical-align: top;
}

.products-table--months {
  min-width: 640px;
}

.product-name-cell strong {
  display: block;
}

.product-name-cell small {
  display: block;
  margin-top: 4px;
  color: #64748b;
}

.source-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.badge-src {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #e2e8f0;
  color: #475569;
}

.badge-src[data-src="woocommerce"] {
  background: #dbeafe;
  color: #1d4ed8;
}

.badge-src[data-src="legacy"] {
  background: #ffedd5;
  color: #c2410c;
}

.badge-src[data-src="manuale"] {
  background: #dcfce7;
  color: #15803d;
}

.badge-src[data-src="importato"] {
  background: #ede9fe;
  color: #6d28d9;
}

.mono {
  font-family: "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.muted {
  color: #64748b;
}

.error-state {
  color: #b91c1c;
  font-weight: 700;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@keyframes icon-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1200px) {
  .filters-grid {
    grid-template-columns: repeat(10, minmax(0, 1fr));
  }

  .field-group,
  .field-group--actions {
    grid-column: span 2;
  }

  .field-group--wide {
    grid-column: span 4;
  }

  .kpi-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .source-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .chart-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 820px) {
  .analytics-hero {
    align-items: stretch;
  }

  .hero-badge {
    width: 100%;
  }

  .filters-grid,
  .focus-filters,
  .focus-summary,
  .kpi-grid,
  .source-grid {
    grid-template-columns: 1fr;
  }

  .field-group,
  .field-group--wide,
  .field-group--actions {
    grid-column: auto;
  }

  .filters-actions {
    justify-content: flex-start;
  }

  .filters-actions .btn--icon {
    width: 46px;
    min-width: 46px;
  }

  .month-chart-row {
    grid-template-columns: 1fr;
  }

  .month-chart-row__bars {
    grid-template-columns: 1fr;
  }

  .products-table {
    min-width: 720px;
  }
}
</style>
